import { InfoCircle, NavArrowRight, WarningTriangle } from 'iconoir-react';
import { FC } from 'react';
import { useIntl } from 'react-intl';

import { BUTTON_TYPES } from '@savgroup-front-common/constants';
import { AssistantStatus } from '@savgroup-front-common/core/components/AssistantBricks/AssistantStatus/AssistantStatus';
import {
  safeFormattedIntlString,
  SafeFormattedMessageWithoutSpread,
} from '@savgroup-front-common/core/formatters';
import {
  Alert,
  AlertDescription,
} from '@savgroup-front-common/primitive/ui/alert';
import { Button } from '@savgroup-front-common/primitive/ui/button';
import { Spinner } from '@savgroup-front-common/primitive/ui/spinner';

import { PublicBar } from '../PublicBar/PublicBar';

import { ActivityQuestion } from './ActivityQuestion/ActivityQuestion';
import messages from './messages';
import { QUESTIONS } from './OnboardingActivities.constant';
import { formatChangedGroups } from './OnboardingActivities.formatters';
import { useOnboardingActivities } from './OnboardingActivities.hooks';
import { ProfileSummary } from './ProfileSummary/ProfileSummary';

const OnboardingActivities: FC = () => {
  const intl = useIntl();
  const {
    selection,
    proposedSelection,
    summary,
    confidence,
    companyName,
    isProfileLoading,
    hasSuggestionFailed,
    hasProposal,
    isUnrelatedActivity,
    isComplete,
    changedGroups,
    isSaving,
    sectionRefs,
    getIsGroupRevealed,
    getHaloStatus,
    handleToggle,
    handleSubmit,
  } = useOnboardingActivities();

  const companyLabel =
    companyName || safeFormattedIntlString(messages.yourCompany);
  const changes = formatChangedGroups({ intl, changedGroups });

  return (
    <div className="min-h-screen bg-surface-base-default">
      <PublicBar />
      <main className="mx-auto w-full max-w-280 px-4 pt-8 pb-14 md:px-14 md:pt-12 md:pb-18">
        <h1 className="mb-3.5 text-2xl font-bold tracking-tight stext-base-default">
          <SafeFormattedMessageWithoutSpread message={messages.title} />
        </h1>
        <p className="mb-8 text-base leading-normal stext-muted-default">
          {hasProposal && (
            <SafeFormattedMessageWithoutSpread
              message={messages.leadWithProposal}
              values={{ companyName: companyLabel }}
            />
          )}
          {!hasProposal && (
            <SafeFormattedMessageWithoutSpread
              message={messages.leadWithoutProposal}
            />
          )}
        </p>

        {hasSuggestionFailed && (
          <Alert
            variant="warning"
            className="mb-8"
            data-testid="OnboardingActivities-suggestionFailed"
          >
            <WarningTriangle aria-hidden className="size-4" />
            <AlertDescription>
              <SafeFormattedMessageWithoutSpread
                message={messages.suggestionFailed}
              />
            </AlertDescription>
          </Alert>
        )}
        {isUnrelatedActivity && (
          <Alert
            variant="warning"
            className="mb-8"
            data-testid="OnboardingActivities-unrelatedActivity"
          >
            <WarningTriangle aria-hidden className="size-4" />
            <AlertDescription>
              <SafeFormattedMessageWithoutSpread
                message={messages.unrelatedActivity}
              />
            </AlertDescription>
          </Alert>
        )}
        <ProfileSummary summary={summary} confidence={confidence} />

        <div className="flex flex-col gap-10">
          {QUESTIONS.map(
            ({
              group,
              title,
              description,
              options,
              gridClassName,
              dataTestId,
            }) => (
              <div
                key={group}
                ref={(node) => {
                  sectionRefs.current[group] = node;
                }}
              >
                <ActivityQuestion
                  title={title}
                  description={description}
                  options={options}
                  selectedValues={selection[group]}
                  proposedValues={proposedSelection[group]}
                  haloStatus={getHaloStatus(group)}
                  isRevealed={getIsGroupRevealed(group)}
                  gridClassName={gridClassName}
                  onToggle={(value) => handleToggle({ group, value })}
                  dataTestId={dataTestId}
                />
              </div>
            ),
          )}
        </div>

        {changedGroups.length > 0 && (
          <Alert
            className="mt-6"
            data-testid="OnboardingActivities-changedSelection"
          >
            <InfoCircle aria-hidden className="size-4" />
            <AlertDescription>
              <SafeFormattedMessageWithoutSpread
                message={messages.changedSelection}
                values={{ changes }}
              />
            </AlertDescription>
          </Alert>
        )}

        <div className="mt-11 flex justify-end">
          <Button
            type={BUTTON_TYPES.BUTTON}
            size="lg"
            className="w-full px-5 sm:w-auto"
            disabled={!isComplete || isSaving}
            onClick={handleSubmit}
            data-testid="OnboardingActivities-submit"
          >
            {isSaving && <Spinner />}
            <SafeFormattedMessageWithoutSpread message={messages.confirm} />
            <NavArrowRight aria-hidden className="size-4" />
          </Button>
        </div>
      </main>

      {isProfileLoading && (
        <AssistantStatus dataTestId="OnboardingActivities-prefilling">
          <SafeFormattedMessageWithoutSpread
            message={messages.prefilling}
            values={{ companyName: companyLabel }}
          />
        </AssistantStatus>
      )}
    </div>
  );
};

OnboardingActivities.displayName = 'OnboardingActivities';

export default OnboardingActivities;
